// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.

import React from "react";
import "./App.css";
import * as microsoftTeams from "@microsoft/teams-js";
//import { Avatar, Loader } from "@fluentui/react-northstar";
import { postData } from "./common/utils";
import logo from "../assets/images/pats-logo.svg";
import { encryptData } from "./../utils/functions";

/**
 * The 'PersonalTab' component renders the main tab content
 * of your app.
 */
class Tab extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      context: {},
      ssoToken: "",
      consentRequired: false,
      consentProvided: false,
      graphAccessToken: "",
      photo: "",
      error: false,
      testing: "Loading...",
      percent: 0,
    };



    //Bind any functions that need to be passed as callbacks or used to React components
    this.ssoLoginSuccess = this.ssoLoginSuccess.bind(this);
    this.ssoLoginFailure = this.ssoLoginFailure.bind(this);
    this.consentSuccess = this.consentSuccess.bind(this);
    this.consentFailure = this.consentFailure.bind(this);
    this.unhandledFetchError = this.unhandledFetchError.bind(this);
    this.callGraphFromClient = this.callGraphFromClient.bind(this);
    this.showConsentDialog = this.showConsentDialog.bind(this);
  }

  //React lifecycle method that gets called once a component has finished mounting
  //Learn more: https://reactjs.org/docs/react-component.html#componentdidmount
  componentDidMount() {
    // Initialize the Microsoft Teams SDK
    microsoftTeams.initialize();

    // Get the user context from Teams and set it in the state
    microsoftTeams.getContext((context, error) => {
      this.setState({ context: context });
    });

    //Perform single sign-on authentication
    let authTokenRequestOptions = {
      successCallback: (result) => {
        this.ssoLoginSuccess(result);
      }, //The result variable is the SSO token.
      failureCallback: (error) => {
        this.ssoLoginFailure(error);
      },
    };

    microsoftTeams.authentication.getAuthToken(authTokenRequestOptions);
  }

  ssoLoginSuccess = async (result) => {
    //console.log("SSO success: " + result);
    this.setState({ ssoToken: result });
    this.exchangeClientTokenForServerToken(result);
  };

  ssoLoginFailure(error) {
    console.error("SSO failed: ", error);
    this.setState({ error: true });
  }

  updateProgress = (field, val) => {
    this.setState({ [field]: val });
  };

  //Exchange the SSO access token for a Graph access token
  //Learn more: https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-oauth2-on-behalf-of-flow
  //#region API Response Structure
  /* 
  {"data":"{\"error\":\"invalid_grant\",\"error_description\":\"AADSTS65001: The user or administrator has not consented to use the application with ID '7b9df68d-0a19-44ab-9868-1d8fafff50ec' named 'Pats'. Send an interactive authorization request for this user and resource.\\r\\nTrace ID: 19aa1045-bf40-4c3a-a8c9-4f993234a602\\r\\nCorrelation ID: cfcf3e3e-f0be-4d0a-9d5b-f3b16f3a43ee\\r\\nTimestamp: 2021-08-09 12:29:24Z\",\"error_codes\":[65001],\"timestamp\":\"2021-08-09 12:29:24Z\",\"trace_id\":\"19aa1045-bf40-4c3a-a8c9-4f993234a602\",\"correlation_id\":\"cfcf3e3e-f0be-4d0a-9d5b-f3b16f3a43ee\",\"suberror\":\"consent_required\"}","status":400}
  */

  /*
  {
   "@odata.context":"https://graph.microsoft.com/v1.0/$metadata#users/$entity",
   "@odata.id":"https://graph.microsoft.com/v2/44060820-ecc9-4366-b97e-0db725e356db/directoryObjects/607529b6-0235-42a0-a8cf-a6741a437657/Microsoft.DirectoryServices.User",
   "businessPhones":[
      
   ],
   "displayName":"Milan Zaveri",
   "givenName":"Milan",
   "jobTitle":"Web Developer",
   "mail":"milan.zaveri@i2econsulting.com",
   "mobilePhone":"9699110880",
   "officeLocation":null,
   "preferredLanguage":null,
   "surname":"Zaveri",
   "userPrincipalName":"milan.zaveri@i2econsulting.com",
   "id":"607529b6-0235-42a0-a8cf-a6741a437657"
  }
  */
  //#endregion
  exchangeClientTokenForServerToken = async (token) => {
    let serverURL = `${process.env.REACT_APP_API_URL}api/user/getGraphAccessToken?ssoToken=${token}`;
    let response = await fetch(serverURL).catch(this.unhandledFetchError); //This calls getGraphAccessToken route in /api-server/app.js
    //this.setState({testing:response});
    response = await response.json().catch(this.unhandledFetchError);
    //console.log(response);

    if (
      response.issuccessful === false &&
      response.error === "consent_required"
    ) {
      //A consent_required error means it's the first time a user is logging into to the app, so they must consent to sharing their Graph data with the app.
      //They may also see this error if MFA is required.
      this.setState({ consentRequired: true }); //This displays the consent required message.
      this.showConsentDialog(); //Proceed to show the consent dialogue.
    } else if (response.issuccessful === false) {
      //Unknown error
      //console.error(data);
      this.setState({ error: true });
    } else {
      //Server side token exchange worked. Save the access_token to state, so that it can be picked up and used by the componentDidMount lifecycle method.
      this.setState({ graphAccessToken: response.data["access_token"] });
    }
  };

  //Show a popup dialogue prompting the user to consent to the required API permissions. This opens ConsentPopup.js.
  //Learn more: https://docs.microsoft.com/en-us/microsoftteams/platform/tabs/how-to/authentication/auth-tab-aad#initiate-authentication-flow
  showConsentDialog() {
    microsoftTeams.authentication.authenticate({
      url: window.location.origin + "/auth-start",
      width: 600,
      height: 535,
      successCallback: (result) => {
        this.consentSuccess(result);
      },
      failureCallback: (reason) => {
        this.consentFailure(reason);
      },
    });
  }

  //Callback function for a successful authorization
  consentSuccess(result) {
    //Save the Graph access token in state
    this.setState({
      graphAccessToken: result,
      consentProvided: true,
    });
  }

  consentFailure(reason) {
    console.error("Consent failed: ", reason);
    this.setState({ error: true });
  }

  //React lifecycle method that gets called after a component's state or props updates
  //Learn more: https://reactjs.org/docs/react-component.html#componentdidupdate
  componentDidUpdate = async (prevProps, prevState) => {
    //Check to see if a Graph access token is now in state AND that it didn't exist previously
    if (
      prevState.graphAccessToken === "" &&
      this.state.graphAccessToken !== ""
    ) {
      this.callGraphFromClient();
    }
  };

  // Fetch the user's profile photo from Graph using the access token retrieved either from the server
  // or microsoftTeams.authentication.authenticate
  callGraphFromClient = async () => {
    let upn = this.state.context["upn"];
    let graphProfileEndpoint = `https://graph.microsoft.com/v1.0/me/`;
    let graphRequestParams = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        authorization: "bearer " + this.state.graphAccessToken,
      },
    };


    let response = await fetch(graphProfileEndpoint, graphRequestParams).catch(
      this.unhandledFetchError
    );
    if (response.ok) {
      response = await response.json().catch(this.unhandledFetchError);
      this.updateProgress("percent", this.state.percent + 20);

      //Get Manager and Department
      let graphManagerEndpoint = `https://graph.microsoft.com/v1.0/me/manager`;
      let graphManagerRequestParams = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: "bearer " + this.state.graphAccessToken,
        },
      };
      let graphManagerResponse = await fetch(
        graphManagerEndpoint,
        graphManagerRequestParams
      ).catch(this.unhandledFetchError);
      graphManagerResponse = await graphManagerResponse
        .json()
        .catch(this.unhandledFetchError);
      //console.log(graphManagerResponse);

      //Get Department
      let graphDepartmentEndpoint = `https://graph.microsoft.com/v1.0/me/department`;
      let graphDepartmentRequestParams = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: "bearer " + this.state.graphAccessToken,
        },
      };
      let graphDepartmentResponse = await fetch(
        graphDepartmentEndpoint,
        graphDepartmentRequestParams
      ).catch(this.unhandledFetchError);
      graphDepartmentResponse = await graphDepartmentResponse
        .json()
        .catch(this.unhandledFetchError);
      //console.log(graphDepartmentResponse);

      this.updateProgress("percent", this.state.percent + 20);
      //Success. Got user data from ms graph
      //Now get user profile picture
      //Get User profile picture
      let graphPhotoEndpoint = `https://graph.microsoft.com/v1.0/me/photo/$value`;
      let graphPhotoRequestParams = {
        method: "GET",
        headers: {
          "Content-Type": "image/jpg",
          authorization: "bearer " + this.state.graphAccessToken,
        },
      };

      this.updateProgress("percent", this.state.percent + 20);

      let graphResponse = await fetch(
        graphPhotoEndpoint,
        graphPhotoRequestParams
      ).catch(this.unhandledFetchError);
      var imageBase64 = null;
      var imageBlob = null;
      if (graphResponse.ok) {
        // console.error("ERROR: ", response);
        // this.setState({error:true});
        //this.setState({testing:'error profile picture'});
        imageBlob = await graphResponse.blob().catch(this.unhandledFetchError); //Get image data as raw binary data
      }
      var formData = new FormData();
      formData.append("userImage", imageBlob);
      formData.append("mail", encryptData(response.mail));
      formData.append("displayName", response.displayName);
      formData.append("jobTitle", response.jobTitle);
      formData.append("managerMail", graphManagerResponse.mail);
      formData.append("managerDisplayName", graphManagerResponse.displayName);
      formData.append("department", graphDepartmentResponse.value);

      this.updateProgress("percent", 100);

      //#region Test SSO
      const response2 = await fetch(
        process.env.REACT_APP_API_URL + "api/user/sso3",
        {
          method: "POST", // *GET, POST, PUT, DELETE, etc.
          // mode: 'cors', // no-cors, *cors, same-origin
          // cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
          // credentials: 'same-origin', // include, *same-origin, omit
          headers: {
            authorization: "bearer " + this.state.graphAccessToken,
          },
          // redirect: 'follow', // manual, *follow, error
          // referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
          body: formData, // body data type must match "Content-Type" header
        }
      )
        .then((response) => response.json())
        .then((data) => {
          if (data) {
            //localStorage.removeItem("pats_login");
            localStorage.setItem("pats_login", JSON.stringify(data.user));

            //history.push('/home');
            this.props.history.push("/home");
          } else {
            //Login unsuccessful
            this.setState({
              testing: "Login error: " + data.message,
            });
          }
        });

      //Send profile data to backend and register/login user
      //Redirect to home page
      // var userData = {
      //   user: response,
      //   userImage: imageBase64
      // }
      // postData(process.env.REACT_APP_API_URL + 'api/user/sso', userData)
      // .then(data => {
      //   if (data.issuccessful == true) {
      //     localStorage.setItem('pats_login', JSON.stringify(data.user));
      //     //history.push('/home');
      //     this.props.history.push('/home')
      //   }
      //   else {
      //     //Login unsuccessful
      //     this.setState({
      //       testing: 'Login error: ' + data.message
      //     })
      //   }
      // });
    }
  };

  //Generic error handler ( avoids having to do async fetch in try/catch block )
  unhandledFetchError(err) {
    console.error("Unhandled fetch error: ", err);
    this.setState({ error: true });
  }

  render() {
    // let title = Object.keys(this.state.context).length > 0 ?
    //   'Congratulations ' + this.state.context['upn'] + '! This is your tab' : <Loader/>;

    // let ssoMessage = this.state.ssoToken === "" ?
    //   <Loader label='Performing Azure AD single sign-on authentication...'/>: null;

    // let serverExchangeMessage = (this.state.ssoToken !== "") && (!this.state.consentRequired) && (this.state.photo==="") ?
    //   <Loader label='Exchanging SSO access token for Graph access token...'/> : null;

    // let consentMessage = (this.state.consentRequired && !this.state.consentProvided) ?
    //   <Loader label='Consent required.'/> : null;

    // let avatar = this.state.photo !== "" ?
    //   <Avatar image={this.state.photo} size='largest'/> : null;

    // let content;
    // if(this.state.error){
    //   content = <h1>ERROR: Please ensure pop-ups are allowed for this website and retry</h1>
    // } else {
    //   content =
    //     <div>
    //       <h1>{title}</h1>
    //       <h3>{ssoMessage}</h3>
    //       <h3>{serverExchangeMessage}</h3>
    //       <h3>{consentMessage}</h3>
    //       <h1>{avatar}</h1>
    //     </div>
    // }

    return (
      <div className="loading-image-bg">
        <div>
          <div className="loading-image-logo">
            <img src={logo} alt="logo" />
          </div>
          <div className="loading-image-loader">
            <div
              className="loading-image-loader-load"
              style={{ width: this.state.percent + '%' }}
            ></div>
          </div>
          <div className="loading-image-text">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }
}

export default Tab;
